import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, FileText, Trash2 } from 'lucide-react';
import { Label } from './ui/label';

interface NewSessionWarningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (clearDocuments: boolean) => void;
  onSaveFirst: () => void;
  hasUnsavedChanges: boolean;
  hasDocuments: boolean;
  documentCount?: number;
}

export function NewSessionWarningModal({
  open,
  onOpenChange,
  onConfirm,
  onSaveFirst,
  hasUnsavedChanges,
  hasDocuments,
  documentCount = 0,
}: NewSessionWarningModalProps) {
  const [clearDocuments, setClearDocuments] = useState(false);

  const handleContinue = () => {
    onConfirm(clearDocuments);
    onOpenChange(false);
    setClearDocuments(false); // Reset for next time
  };

  const handleCancel = () => {
    onOpenChange(false);
    setClearDocuments(false); // Reset for next time
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <DialogTitle>Start New Session?</DialogTitle>
            </div>
          </div>
          <DialogDescription className="pt-2">
            {hasUnsavedChanges
              ? "Your current session will be lost. Save it first if you want to keep it."
              : "This will start a fresh session."}
          </DialogDescription>
        </DialogHeader>

        {hasDocuments && (
          <div className="space-y-3 py-2">
            <Label className="text-sm font-medium text-gray-900">
              Active Documents ({documentCount})
            </Label>
            <div className="space-y-2">
              <button
                onClick={() => setClearDocuments(false)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  !clearDocuments
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <FileText className="w-5 h-5 text-blue-600" />
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-gray-900">Keep documents</div>
                  <div className="text-xs text-gray-500">Continue with loaded documents in context</div>
                </div>
              </button>

              <button
                onClick={() => setClearDocuments(true)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                  clearDocuments
                    ? 'border-red-500 bg-red-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <Trash2 className="w-5 h-5 text-red-600" />
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-gray-900">Clear all documents</div>
                  <div className="text-xs text-gray-500">Remove all documents and start fresh</div>
                </div>
              </button>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
          {hasUnsavedChanges && (
            <Button
              variant="outline"
              onClick={() => {
                onSaveFirst();
                onOpenChange(false);
                setClearDocuments(false);
              }}
              className="flex-1 sm:flex-none"
            >
              Save first
            </Button>
          )}
          <Button
            onClick={handleContinue}
            className="flex-1 sm:flex-none"
          >
            Start new session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
